import {
  createContext,
  FC,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "../constants/routes";
import { useCompanyStore } from "../stores/useCompanyStore";
import { SubscriptionOptionEnum } from "../modules/private/settings-module/billing-and-packages/shared/types";

interface IPartnerLayoutContext {
  isPartnerMode: boolean;
  setIsPartnerMode: (val: boolean) => void;
  handleSwitchToPartnerMode: () => void;
}

const PartnerLayoutContext = createContext<IPartnerLayoutContext>({
  isPartnerMode: false,
  setIsPartnerMode: () => null,
  handleSwitchToPartnerMode: () => null,
});

export const PartnerLayoutContextProvider: FC<{ children: ReactElement }> = ({
  children,
}) => {
  const { company } = useCompanyStore();

  const navigate = useNavigate();

  const hasPaymentsOnlySubscription =
    company?.currentSubscription?.details?.subscriptionOption ===
    SubscriptionOptionEnum.PAYMENTS_ONLY;
  const canManageCompanies = company?.canManageCompanies;
  const isManagedByPartner = company?.managedBy;
  const [isPartnerMode, setIsPartnerMode] = useState(() => {
    const storedData = localStorage.getItem("isPartnerMode");
    try {
      return storedData ? JSON.parse(storedData) : null;
    } catch (error: any) {
      console.error("Error parsing from local storage:", error);
      return null;
    }
  });

  useEffect(() => {
    localStorage.setItem("isPartnerMode", JSON.stringify(isPartnerMode));
  }, [isPartnerMode]);

  useEffect(() => {
    if (company) {
      if (!canManageCompanies && isPartnerMode) {
        handleSwitchToPartnerMode();
      }
    }
  }, [canManageCompanies, isPartnerMode, company]);

  const handleSwitchToPartnerMode = () => {
    if (location.pathname.includes("private/partner")) {
      setIsPartnerMode(false);
      let destination;
      if (hasPaymentsOnlySubscription || isManagedByPartner) {
        destination = AppRoutes.Private.Payments.TRANSACTIONS_LIST;
      } else if (window.innerWidth <= 576) {
        destination = AppRoutes.Private.Inventory.ORDER_GENERATION_EPOS;
      } else {
        destination = AppRoutes.Private.Inventory.ORDERS_LIST;
      }
      navigate(destination);
    } else {
      setIsPartnerMode(true);
      navigate(AppRoutes.Private.Partner.DASHBOARD);
    }
  };

  return (
    <PartnerLayoutContext.Provider
      value={{
        isPartnerMode,
        setIsPartnerMode,
        handleSwitchToPartnerMode,
      }}
    >
      {children}
    </PartnerLayoutContext.Provider>
  );
};

export const usePartnerLayoutContext = () => {
  return useContext(PartnerLayoutContext);
};
